import { ChangeDetectionStrategy, Component, OnDestroy, OnInit, ViewChild, computed, inject } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';

import { Store } from '@ngrx/store';
import {
  combineLatest,
  Observable,
  of,
  ReplaySubject,
  Subscription,
  distinctUntilChanged,
  filter,
  map,
  startWith,
  switchMap,
  take,
  tap,
  catchError,
  NEVER,
  Subject,
} from 'rxjs';

import { CustomIcon, IconLibrary, SvgLibraryIcon } from '@finnairoyj/fcom-ui-styles';

import { GtmService } from '@fcom/common/gtm';
import { LanguageService } from '@fcom/ui-translate';
import { AppState, ConfigService, CountryState, StorageService, unsubscribe, WindowRef } from '@fcom/core';
import { loginIsOpen, loginStatus } from '@fcom/core/selectors';
import { LoginStatus, LoginStep } from '@fcom/core-api/login';
import { isPresent, stopPropagation } from '@fcom/core/utils';
import { finShare, snapshot } from '@fcom/rx';
import { CmsTemplate, CmsViewType } from '@fcom/core-api/interfaces';
import { ButtonMode, ButtonSize, ButtonTheme, IconPosition } from '@fcom/ui-components';
import { LoginActions } from '@fcom/core/actions';
import { DapiService } from '@fcom/dapi/api/dapi.service';
import { fetchOrderPreviews } from '@fcom/dapi/api/order-functions';

import { CmsPageService, MediaQueryService, NavigationMenuService, FinnairThemeService } from '../../services';
import { FinnairTheme } from '../../interfaces/theme.interface';
import {
  BreadcrumbItem,
  ConsentStatus,
  ElementActions,
  ElementTypes,
  GaContext,
  MenuItem,
  MenuJson,
  onOpenGtmEvent,
} from '../../interfaces';
import { ConsentService, JoinService, LoginService, LoginWidgetComponent } from '../../login';
import { getMobileAppVerificationSuccessUrl, MOBILE_APP_CHANNEL } from '../../login/interfaces';
import { isNavigationEvent } from '../../utils';
import { LanguageSelectorContext } from '../language-selector/language-selector.component';

interface ExtendedMenuItem extends MenuItem {
  trackingId: string;
}

const UPCOMING_TRIPS_STORAGE_KEY = 'amountOfUpcomingTrips';
const UPCOMING_TRIPS_MAX_AMOUNT = 5;

@Component({
  selector: 'fin-header',
  styleUrls: ['./header.component.scss'],
  templateUrl: './header.component.html',
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HeaderComponent implements OnInit, OnDestroy {
  private navigationMenuService = inject(NavigationMenuService);
  private languageService = inject(LanguageService);
  private configService = inject(ConfigService);
  private loginService = inject(LoginService);
  private store$ = inject<Store<AppState>>(Store);
  private mediaQueryService = inject(MediaQueryService);
  private gtmService = inject(GtmService);
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private pageService = inject(CmsPageService);
  private consentService = inject(ConsentService);
  joinService = inject(JoinService);
  private dapiService = inject(DapiService);
  private storageService = inject(StorageService);
  private readonly windowRef = inject(WindowRef);
  private themeService = inject(FinnairThemeService);

  isNewBrand = computed(() => this.themeService.siteTheme() !== FinnairTheme.LEGACY);

  @ViewChild(LoginWidgetComponent, { static: false }) loginWidget: LoginWidgetComponent;

  readonly ButtonSize = ButtonSize;
  readonly CustomIcon = CustomIcon;
  readonly IconLibrary = IconLibrary;
  readonly IconPosition = IconPosition;
  readonly ButtonMode = ButtonMode;
  readonly ButtonTheme = ButtonTheme;
  readonly ElementTypes = ElementTypes;
  readonly GaContext = GaContext;
  readonly homeLink$: Observable<string>;
  readonly currentLanguage$: Observable<string>;
  readonly LoginStatus = LoginStatus;
  readonly enableWelcomeBackMessage: boolean = false;
  readonly isVisible$: Observable<boolean>;
  readonly LANGUAGE_SELECTOR_CONTEXT = LanguageSelectorContext.TOP_NAVI;
  readonly SvgLibraryIcon = SvgLibraryIcon;

  isMenuOpen$: Observable<boolean>;
  countries$: Observable<CountryState>;
  loginStatus$: Observable<LoginStatus>;
  oneworldLink$: Observable<BreadcrumbItem>;

  menuItems$: Observable<MenuJson>;
  topMenuItems$: Observable<ExtendedMenuItem[]>;
  expandedCategory$: Observable<MenuItem>;
  expandedOrActiveNaviCategory$: Observable<MenuItem>;
  activeNaviCategory$: Observable<MenuItem>;
  isOpen = false;
  pageHasDisruptions$: Observable<boolean>;
  enableLoginDialog = false;
  enableJoinDialog = false;
  loginDialogOpen$: Observable<boolean>;
  authOverlayOpen$: Observable<boolean>;
  cookiePolicy$: Observable<boolean>;
  mobileOrTablet$: Observable<boolean>;
  isLandingPage$: Observable<boolean>;
  welcomeBackMessageOpen$: Observable<boolean>;
  amountOfUpcomingTrips$: Observable<string>;
  accountVerificationToken$ = new ReplaySubject<string>(1);
  verificationChannel$ = new ReplaySubject<string>(1);
  verificationSuccessUrl$ = new ReplaySubject<string | undefined>(1);
  subscriptions = new Subscription();
  loginWidgetOpen$ = new Subject<boolean>();

  constructor() {
    this.isVisible$ = this.navigationMenuService.isHeaderVisible$;
    this.isMenuOpen$ = this.navigationMenuService.isNavigationMenuOpen$;
    this.homeLink$ = this.languageService.lang.pipe(map((lang) => `/${lang}`));
    this.enableWelcomeBackMessage = this.configService.cfg.enableWelcomeBackMessage;
    this.enableLoginDialog = this.configService.cfg.enableLoginDialog;
    this.enableJoinDialog = this.configService.cfg.enableJoinDialog;
  }

  ngOnInit(): void {
    this.loginStatus$ = this.store$.pipe(loginStatus(), finShare());
    this.oneworldLink$ = this.navigationMenuService.oneworldLink$;
    this.menuItems$ = this.navigationMenuService.menuItems$;
    const matchUrlLocalRegex = /^\/\w+-\w+\//;
    this.topMenuItems$ = this.menuItems$.pipe(
      map((menuItems) =>
        menuItems.top.map((menuItem) => ({
          ...menuItem,
          trackingId: menuItem.link.match(matchUrlLocalRegex) ? menuItem.link.replace(matchUrlLocalRegex, '') : 'book',
        }))
      )
    );
    this.expandedCategory$ = this.navigationMenuService.expandedNaviCategory;
    this.expandedOrActiveNaviCategory$ = this.navigationMenuService.expandedOrActiveNaviCategory$;
    this.activeNaviCategory$ = this.navigationMenuService.activeNaviCategory;
    const pagePath$ = this.router.events.pipe(
      filter<NavigationEnd>(isNavigationEvent),
      map((navigation) => navigation.url),
      startWith(this.router.url),
      distinctUntilChanged()
    );

    const amountOfUpcomingTrips$: Observable<number> = this.loginStatus$.pipe(
      filter((status) => status === LoginStatus.LOGGED_IN),
      switchMap(() =>
        this.dapiService
          .invoke$(fetchOrderPreviews, this.configService.cfg.orderUrl, {
            locale: this.languageService.localeValue,
          })
          .pipe(take(1))
      ),
      catchError(() => NEVER),
      map((response) => response?.orderPreviews?.length || 0),
      tap((orderPreviews) => {
        this.storageService.SESSION.set(UPCOMING_TRIPS_STORAGE_KEY, orderPreviews);
      }),
      startWith(this.storageService.SESSION.get(UPCOMING_TRIPS_STORAGE_KEY) || 0),
      finShare()
    );

    this.amountOfUpcomingTrips$ = amountOfUpcomingTrips$.pipe(
      map((amount) => {
        if (amount > 0) {
          return amount <= UPCOMING_TRIPS_MAX_AMOUNT ? amount.toString() : `${UPCOMING_TRIPS_MAX_AMOUNT}+`;
        }
        return '';
      }),
      finShare()
    );

    this.pageHasDisruptions$ = pagePath$.pipe(
      switchMap((path) => this.pageService.getPageDataIfExists(path)),
      filter<CmsTemplate>(Boolean),
      map((template) => template.main),
      map((contents) =>
        contents.find(
          ({ viewTypeName }) => isPresent(viewTypeName) && viewTypeName.endsWith(CmsViewType.FRONT_PAGE_DISRUPTIONS)
        )
      ),
      map((disruptions) => disruptions?.items.length > 0),
      startWith(false)
    );

    this.cookiePolicy$ = this.consentService.cookieConsents$.pipe(
      map((cg) => cg?.consents?.every((c) => c.consentStatus !== ConsentStatus.WITHDRAWN)),
      startWith(false),
      finShare()
    );

    this.loginDialogOpen$ = combineLatest([this.loginStatus$, this.store$.pipe(loginIsOpen())]).pipe(
      map(([status, isOpen]) => status !== LoginStatus.INITIAL && status !== LoginStatus.PENDING && isOpen)
    );
    const mobile$ = this.mediaQueryService.isBreakpoint$('mobile');
    const tablet$ = this.mediaQueryService.isBreakpoint$('tablet');
    this.mobileOrTablet$ = combineLatest([mobile$, tablet$]).pipe(
      map(([mobile, tablet]) => mobile || tablet),
      startWith(false)
    );

    this.authOverlayOpen$ = combineLatest([
      this.loginDialogOpen$,
      this.joinService.joinDialogOpen$,
      mobile$,
      this.loginWidgetOpen$.pipe(startWith(false)),
    ]).pipe(
      map(
        ([loginDialogOpen, joinDialogOpen, mobile, loginWidgetOpen]) =>
          !mobile && (loginDialogOpen || joinDialogOpen || loginWidgetOpen)
      ),
      distinctUntilChanged(),
      finShare()
    );

    this.isLandingPage$ = pagePath$.pipe(map((routeValue) => new RegExp('^/[^/]+$').test(routeValue)));

    this.welcomeBackMessageOpen$ = combineLatest([
      this.loginDialogOpen$,
      this.joinService.joinDialogOpen$,
      this.pageHasDisruptions$,
      this.cookiePolicy$,
      of(this.enableWelcomeBackMessage),
      this.loginStatus$,
      this.isLandingPage$,
      this.mobileOrTablet$,
    ]).pipe(
      map(
        ([
          loginDialogOpen,
          joinDialogOpen,
          pageHasDisruptions,
          cookiePolicy,
          enableWelcomeBackMessage,
          loginStatus,
          isLandingPage,
          mobileOrTablet,
        ]) =>
          !loginDialogOpen &&
          !joinDialogOpen &&
          !pageHasDisruptions &&
          cookiePolicy &&
          enableWelcomeBackMessage &&
          loginStatus === LoginStatus.NOT_LOGGED_IN &&
          isLandingPage &&
          !mobileOrTablet
      )
    );

    const token = this.route.snapshot.queryParamMap.get('verify-account') ?? '';
    this.accountVerificationToken$.next(token);
    const channelParam = this.configService.cfg.enableMobileAppVerificationRedirect
      ? (this.route.snapshot.queryParamMap.get('channel') ?? '').toLowerCase()
      : '';
    const channel = channelParam === MOBILE_APP_CHANNEL ? MOBILE_APP_CHANNEL : '';
    const verificationSuccessUrl =
      channel === MOBILE_APP_CHANNEL
        ? getMobileAppVerificationSuccessUrl(
            this.route.snapshot.queryParamMap.get('app-id'),
            this.configService.cfg.mobileAppVerificationSuccessUrls
          )
        : undefined;
    this.verificationChannel$.next(channel);
    this.verificationSuccessUrl$.next(verificationSuccessUrl);
    if (token) {
      this.store$.dispatch(LoginActions.setLoginPhaseStep({ step: LoginStep.CREDENTIALS }));
    }
  }

  ngOnDestroy(): void {
    unsubscribe(this.subscriptions);
  }

  openLoginWidget(event: MouseEvent): void {
    stopPropagation(event);
    this.loginWidget.toggle();
  }

  closeNaviMenu(): void {
    this.navigationMenuService.closeNaviMenu();
    this.isOpen = false;
  }

  setCategoryAsExpanded(event: Event, item: MenuItem): void {
    this.navigationMenuService.setCategoryAsExpanded(event, item);
  }

  clickOutside(): void {
    this.isOpen = false;
    this.navigationMenuService.closeNaviMenu();
  }

  handleLoginButtonClick(event: MouseEvent, loginStatus: LoginStatus): void {
    if (loginStatus === LoginStatus.LOGGED_IN) {
      this.openLoginWidget(event);
    }

    if (loginStatus === LoginStatus.NOT_LOGGED_IN) {
      this.openLoginDialog();
    }
  }

  toggleNavigation(event: Event): void {
    stopPropagation(event);

    this.isOpen = !this.isOpen;

    if (this.isOpen) {
      this.navigationMenuService.setExpandedCategory(snapshot(this.expandedOrActiveNaviCategory$));
    } else {
      this.navigationMenuService.closeNaviMenu();
    }

    this.gtmService.trackElement(
      'desktop-main-navigation-hamburger-navi',
      GaContext.DESKTOP_MAIN_NAVIGATION,
      ElementTypes.ACCORDION,
      undefined,
      this.isOpen ? ElementActions.OPEN : ElementActions.CLOSE
    );
  }

  headerGtmClickEvent(gtmId: string, gtmContext: GaContext): void {
    this.gtmService.trackElement(gtmId, gtmContext, ElementTypes.BUTTON, undefined, ElementActions.CLICK);
  }

  joinFinnairPlus(): void {
    if (this.enableJoinDialog) {
      this.loginWidget.close();
      this.joinService.joinDialogOpen$.next(true);
      this.headerGtmClickEvent(`join-form-open-${onOpenGtmEvent}`, GaContext.FINNAIR_PLUS);
    } else {
      this.loginService.redirectToJoin();
    }
  }

  openLoginDialog(): void {
    if (this.enableLoginDialog) {
      this.loginWidget.close();
      this.joinService.joinDialogOpen$.next(false);
      this.store$.dispatch(LoginActions.setLoginPhaseStep({ step: LoginStep.CREDENTIALS }));
    } else {
      this.loginService.redirectToLogin();
    }
  }

  closeLoginDialog(): void {
    this.store$.dispatch(LoginActions.clearLoginPhase());
  }

  scrollToTop(): void {
    this.windowRef.nativeWindow.scrollTo({ top: 0, behavior: 'smooth' });
  }

  accountVerificationFinished(): void {
    this.accountVerificationToken$.next(null);
  }
}
